// Product-language formatting (PRD §4.3): whole percentages, month-year dates, everyday terms.

import type { Handle, LinkBody, Step } from "../types";

/** "17%", "<1%", ">99%", or "–" for no value. */
export function pct(x: number | null | undefined): string {
	if (x === null || x === undefined || Number.isNaN(x)) return "–";
	if (x > 0 && x < 0.005) return "<1%";
	if (x < 1 && x > 0.995) return ">99%";
	return `${Math.round(x * 100)}%`;
}

/** A change in chance: "+3 points", "−1 point", "no change". */
export function points(delta: number): string {
	const p = Math.round(delta * 100);
	if (p === 0) return "no change";
	return `${p > 0 ? "+" : "−"}${Math.abs(p)} ${Math.abs(p) === 1 ? "point" : "points"}`;
}

function compact(v: number): string {
	const a = Math.abs(v);
	const sign = v < 0 ? "−" : "";
	const trim = (n: number) => (n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1).replace(/\.0$/, "") : n.toFixed(2).replace(/\.?0+$/, ""));
	if (a >= 1e12) return `${sign}${trim(a / 1e12)}T`;
	if (a >= 1e9) return `${sign}${trim(a / 1e9)}B`;
	if (a >= 1e6) return `${sign}${trim(a / 1e6)}M`;
	if (a >= 1e4) return `${sign}${trim(a / 1e3)}k`;
	return `${sign}${trim(a)}`;
}

/** A metric value in its unit: "$2.4M", "5%", "3.5×", "42 people". */
export function fmtValue(v: number | null | undefined, unit: string): string {
	if (v === null || v === undefined || Number.isNaN(v)) return "–";
	switch (unit) {
		case "USD":
			return v < 0 ? `−$${compact(-v)}` : `$${compact(v)}`;
		case "%":
			return `${Math.round(v * 100)}%`;
		case "x":
			return `${compact(v)}×`;
		case "people":
			return `${Math.round(v)} ${Math.round(v) === 1 ? "person" : "people"}`;
		default:
			return unit ? `${compact(v)} ${unit}` : compact(v);
	}
}

/** Like `fmtValue`, but "people" drops the word. */
export function fmtShort(v: number, unit: string): string {
	if (unit === "people") return String(Math.round(v));
	return fmtValue(v, unit);
}

/** A value formatted for an input box: short and unit-aware, without the unit word. */
export function fmtInput(v: number | null | undefined, unit: string): string {
	if (v === null || v === undefined || Number.isNaN(v)) return "";
	if (unit === "%") return `${+(v * 100).toFixed(2)}%`;
	if (unit === "people") return String(Math.round(v));
	if (unit === "USD") return fmtValue(v, "USD");
	if (unit === "x") return `${+v.toFixed(2)}`;
	return compact(v);
}

/** "3×", "0.3×", "never". */
export function fmtX(x: number): string {
	if (x === 0) return "never";
	const s = x >= 10 ? x.toFixed(0) : x.toFixed(1).replace(/\.0$/, "");
	return `${s}×`;
}

export function fmtMonths(n: number): string {
	return `${n} ${n === 1 ? "month" : "months"}`;
}

export function opWord(op: string): string {
	return ({ "<": "under", "<=": "at or under", ">": "over", ">=": "at or over" } as Record<string, string>)[op] ?? op;
}

/** Plain words for a step's multiplier: "can’t happen", "normal", "2× as likely". */
export function stepWord(x: number): string {
	if (x === 0) return "can’t happen";
	if (Math.abs(x - 1) < 1e-9) return "normal";
	return `${fmtX(x)} as likely`;
}

/** Each step as a labelled band: "under $100k", "$100k–$250k", "$500k+". */
export function stepBands(steps: Step[], unit: string): { label: string; x: number }[] {
	return steps.map((st, i) => {
		const next = steps[i + 1]?.from;
		if (i === 0) return { label: next === undefined ? "always" : `under ${fmtValue(next, unit)}`, x: st.x };
		if (next === undefined) return { label: `${fmtValue(st.from!, unit)}+`, x: st.x };
		return { label: `${fmtValue(st.from!, unit)}–${fmtValue(next, unit)}`, x: st.x };
	});
}

/** A two-step ladder where one side is "normal" reads as a single threshold. */
export function singleThreshold(steps: Step[]): { below: boolean; value: number; x: number } | null {
	if (steps.length !== 2 || steps[1].from === undefined) return null;
	if (steps[1].x === 1 && steps[0].x !== 1) return { below: true, value: steps[1].from, x: steps[0].x };
	if (steps[0].x === 1 && steps[1].x !== 1) return { below: false, value: steps[1].from, x: steps[1].x };
	return null;
}

/** What the language helpers need to know about a node: its title and, for metrics, its unit. */
export type NodeNames = Record<Handle, { title: string; unit?: string }>;

export function titleOf(nodes: NodeNames, h: Handle): string {
	return nodes[h]?.title ?? h;
}

/** The one plain sentence for each link type (PRD §7.4). */
export function linkSentence(l: LinkBody, nodes: NodeNames): string {
	const t = (h: Handle) => titleOf(nodes, h);
	if (l.type === "trigger") {
		const lag = l.lag_months ? ` after a ${fmtMonths(l.lag_months)} delay` : "";
		return `If ${t(l.from)} happens, ${pct(l.p)} chance ${t(l.to)} follows within ${fmtMonths(l.within_months)}${lag}.`;
	}
	if (l.type === "multiplier" && "steps" in l) {
		const unit = nodes[l.from.metric]?.unit ?? "";
		const single = singleThreshold(l.steps);
		if (single)
			return `While ${t(l.from.metric)} is ${single.below ? "under" : "at or over"} ${fmtValue(single.value, unit)}, ${t(l.to)} is ${fmtX(single.x)} as likely.`;
		return `How likely ${t(l.to)} is depends on ${t(l.from.metric)}: ${stepBands(l.steps, unit)
			.map((b) => `${b.label}: ${stepWord(b.x)}`)
			.join("; ")}.`;
	}
	if (l.type === "multiplier") {
		const cond = l.for_months ? `For ${fmtMonths(l.for_months)} after ${t(l.from)}` : `Once ${t(l.from)} happens`;
		return `${cond}, ${t(l.to)} is ${fmtX(l.x)} as likely.`;
	}
	const dir = l.change < 0 ? "drops" : "rises";
	const rec = l.recovery_months ? `, recovering over ${fmtMonths(l.recovery_months)}` : "";
	return `If ${t(l.from)} happens, ${t(l.to)} ${dir} ${Math.round(Math.abs(l.change) * 100)}% over ${fmtMonths(l.over_months)}${rec}.`;
}

/** The analyst-mode gloss for a link type. */
export function analystGloss(l: Pick<LinkBody, "type" | "from">): string {
	if (l.type === "trigger") return "Excess hazard over a window, combined noisy-OR with the base hazard.";
	if (l.type === "multiplier" && typeof l.from !== "string") return "Piecewise-constant hazard multiplier on the metric’s previous-month value.";
	if (l.type === "multiplier") return "Hazard multiplier while the condition holds.";
	return "Multiplicative shock with linear ramp-in and optional linear decay.";
}

/**
 * A metric value as someone would type it, in the node's unit: "25B", "$2.5B", "150k", "5%", "2,500,000", "2.5e10".
 * Returns null when the text isn't a number. `%` metrics are stored as fractions, so "5" and "5%" both mean 0.05.
 */
export function parseAmount(text: string, unit: string): number | null {
	const t = text
		.trim()
		.replace(/(people|persons?|×)$/i, "")
		.replace(/[$,\s]/g, "")
		.replace(/^[−–]/, "-");
	if (!t) return null;
	const m = /^(-?\d*\.?\d+(?:e[+-]?\d+)?)(k|m|bn|b|t)?(%)?$/i.exec(t);
	if (!m) return null;
	let v = Number(m[1]);
	if (!Number.isFinite(v)) return null;
	const mult = ({ k: 1e3, m: 1e6, b: 1e9, bn: 1e9, t: 1e12 } as Record<string, number>)[(m[2] ?? "").toLowerCase()];
	if (mult) v *= mult;
	if (unit === "%") return v / 100;
	if (m[3]) return v / 100;
	return v;
}
